import type { ReactNode } from "react";

// Avisos del sistema de feedback (Bheli.Web.Feedback): mismo HTML, mismas clases y mismos roles que en el portal actual.

export type FeedbackTone = "info" | "success" | "warning" | "error";

const toneClass: Record<FeedbackTone, string> = {
  info: "alert-info",
  success: "alert-success",
  warning: "alert-warning",
  error: "alert-error",
};

/** SectionMessage: aviso de sección (error y advertencia con role=alert; los demás, status). */
export function SectionMessage({
  title,
  message,
  tone = "info",
  action,
}: {
  title?: string;
  message?: string | null;
  tone?: FeedbackTone;
  action?: ReactNode;
}) {
  if (!message?.trim()) return null;
  return (
    <div
      className={`feedback-section alert ${toneClass[tone]}`}
      role={tone === "error" || tone === "warning" ? "alert" : "status"}
    >
      {title?.trim() ? <strong>{title}</strong> : null}
      <p>{message}</p>
      {action}
    </div>
  );
}

/** FieldError: error de un campo, enlazado con aria-describedby por su id. */
export function FieldError({ id, message }: { id: string; message?: string | null }) {
  if (!message?.trim()) return null;
  return (
    <span id={id} className="feedback-field-error" role="alert">
      {message}
    </span>
  );
}

export type FieldIssue = { fieldId: string; message: string };

/** ErrorSummary: resumen de errores con enlaces a cada campo (la ruta actual + #campo). */
export function ErrorSummary({ id, title, issues }: { id: string; title: string; issues: FieldIssue[] }) {
  if (issues.length === 0) return null;
  const page = window.location.href.split("#")[0];
  return (
    <div className="feedback-summary alert alert-error" role="alert" aria-labelledby={id} tabIndex={-1}>
      <strong id={id}>{title}</strong>
      <ul>
        {issues.map((issue) => (
          <li key={issue.fieldId}>
            <a href={`${page}#${issue.fieldId}`}>{issue.message}</a>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Toast: confirmación breve (temporizada salvo que tenga acción). */
export function Toast({
  message,
  tone = "success",
  action,
}: {
  message?: string | null;
  tone?: FeedbackTone;
  action?: ReactNode;
}) {
  if (!message?.trim()) return null;
  return (
    <div
      className={`feedback-toast alert ${tone === "success" ? "alert-success" : "alert-info"} ${action ? "" : "feedback-toast-timed"}`}
      role="status"
      aria-live="polite"
    >
      <span>{message}</span>
      {action}
    </div>
  );
}
